import React, { useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useIr } from 'shared/apollo/pageHooks/useIr';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import styled from 'styled-components';
import toCamelCase from 'camelcase-keys';
import DonorClaim from './DonorClaim';
import { ImpactReceipt } from './utils';
import { useClient } from 'shared/apollo';
import {
  AnswersBySectionIdResponse,
  getAnswersBySectionId,
} from '../FeedbackDashboard/Feedback/api';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import { answersBySection } from '../FeedbackDashboard/Feedback/FeedbackReport';
import { FeedbackDashboardCardProps } from 'shared/components/Feedback/FeedbackDashboardCard';
import FeedbackImpactReceipt from '../FeedbackDashboard/Feedback/FeedbackImpactReceipt';
import { ReportStage } from '../FeedbackDashboard/api';
import { Donations, getDonationsById } from 'npo/pages/DonationsBeta/api';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { useIndicator } from 'shared/providers/IndicatorProvider';
import { isCnUserView } from 'shared/utils/partnerUser';

const StyledDiv = styled.div`
  .error-message {
    margin: ${SPACERS.large};
  }
`;

const DonorReceipt: React.FC = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const allocationIdParam = searchParams.get('allocationId');
  const allocationId = allocationIdParam
    ? parseInt(allocationIdParam, 10)
    : null;

  const investmentId: string = useParams<{ investmentId: string }>()
    .investmentId;
  const { data: query, error: hasError, isLoading } = useIr(
    investmentId,
    allocationId,
  );

  if (isLoading) {
    return <TrueImpactLoading />;
  }

  if (hasError) {
    return (
      <StyledDiv>
        <TrueImpactText color="critical" className="error-message">
          Unable to load the impact receipt.
        </TrueImpactText>
      </StyledDiv>
    );
  }

  const ir: ImpactReceipt = toCamelCase(query, { deep: true });
  if (!ir.report?.id) {
    return (
      <StyledDiv>
        <TrueImpactText color="critical" className="error-message">
          There are no reports attached to this investment.
        </TrueImpactText>
      </StyledDiv>
    );
  }
  return <DonorReceiptBase ir={ir} investmentId={investmentId} />;
};

interface DonorReceiptBaseProps {
  ir: ImpactReceipt;
  investmentId: string;
}

const DonorReceiptBase: React.FC<DonorReceiptBaseProps> = ({
  ir,
  investmentId,
}) => {
  const client = useClient();
  const { isLoading, loadReportIndicators } = useIndicator();
  const [requestState, makeRequest] = useGraphQLRequest<
    AnswersBySectionIdResponse
  >();
  const [requestDonationState, makeDonationRequest] = useGraphQLRequest<
    Donations
  >();

  useEffect(() => {
    if (!client) return;
    loadReportIndicators(ir.report.id);
    makeRequest(getAnswersBySectionId(client, ir.report.id));
    makeDonationRequest(getDonationsById(client, investmentId));
  }, [client, ir.report.id, loadReportIndicators]);

  // Apply CN theme for CN users
  useEffect(() => {
    const root = document.getElementById('root');

    if (root && isCnUserView() && ir.report?.cnOptedIn) {
      root.classList.add('cnTheme');
    }

    return () => {
      if (root) {
        root.classList.remove('cnTheme');
      }
    };
  }, [ir.report?.cnOptedIn]);

  const answers = useMemo(
    () => answersBySection(requestState.data?.allNpoReportAnswers || []),
    [requestState],
  );

  if (
    // isLoading ||
    requestState.type === 'REQUEST_INIT' ||
    requestState.type === 'REQUEST_START' ||
    requestDonationState.type === 'REQUEST_INIT' ||
    requestDonationState.type === 'REQUEST_START'
  ) {
    return <TrueImpactLoading />;
  }

  if (
    requestState.type === 'REQUEST_ERROR' ||
    requestDonationState.type === 'REQUEST_ERROR' ||
    requestDonationState.data.allInvestments.length === 0
  ) {
    return (
      <StyledDiv>
        <TrueImpactText color="critical">
          Unable to load the impact receipt
        </TrueImpactText>
      </StyledDiv>
    );
  }

  const investment = requestDonationState.data.allInvestments[0];
  const feedback: FeedbackDashboardCardProps = {
    id: ir.report.id,
    title: ir.report.programName,
    portfolioName: '',
    reportId: ir.report.id,
    contacts: [],
    donorName: ir.accountName,
    dueDate: '',
    status: 'Published',
    year: ir.report.year?.toString(),
    organizationName: ir.accountName,
    lastUpdated: '',
    stage: ir.report.stage?.toUpperCase() as ReportStage,
    totalCost: 0,
    totalDonations: 0,
  };

  const isLegacy = ir.report?.isLegacy;

  return (
    <StyledDiv>
      <FeedbackImpactReceipt
        isThirdParty={false}
        programName={feedback.title || feedback.portfolioName}
        reportId={feedback.reportId}
        answers={answers}
        feedback={feedback}
        isLegacy={isLegacy}
        report={ir?.report}
        ir={ir}
        component={
          <DonorClaim
            ir={ir}
            answers={answers}
            investment={investment}
            isLegacy={isLegacy}
          />
        }
      />
    </StyledDiv>
  );
};

export default DonorReceipt;
